import React, { useEffect, useMemo, useState } from 'react';

const ALL_PARENT_GROUPS = 'All Parent Groups';

const COLUMNS = [
  { key: 'id', label: 'Request - ID' },
  { key: 'macroArea', label: 'Macro Area (SAP) - MA Area' },
  { key: 'parentGroupId', label: 'Parent_Group_ID' },
  { key: 'subGroup', label: 'Sub_Group' },
  { key: 'cumulative', label: 'Cumulative', numeric: true },
  { key: 'textAnswer', label: 'Request - Text Answer' },
  { key: 'textRequest', label: 'Request - Text Request' },
];

function getSortValue(row, key) {
  if (key === 'cumulative') return Number(row.cumulative) || 0;
  const value = row[key];
  if (value == null || value === '') return '';
  return String(value).toLowerCase();
}

function compareSortValues(aVal, bVal, direction) {
  const dir = direction === 'asc' ? 1 : -1;
  const aEmpty = aVal === '';
  const bEmpty = bVal === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return (aVal - bVal) * dir;
  }
  return String(aVal).localeCompare(String(bVal), undefined, { numeric: true }) * dir;
}

function SortIndicator({ active, direction }) {
  if (!active) {
    return (
      <span className="nlp-sort-indicator nlp-sort-indicator-idle" aria-hidden="true">
        ↕
      </span>
    );
  }
  return (
    <span className="nlp-sort-indicator" aria-hidden="true">
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  );
}

function Section5TicketRecords({ rows, visible }) {
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [parentGroupFilter, setParentGroupFilter] = useState(ALL_PARENT_GROUPS);

  const parentGroupOptions = useMemo(() => {
    const unique = Array.from(
      new Set((rows || []).map((row) => row.parentGroupId).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return [ALL_PARENT_GROUPS, ...unique];
  }, [rows]);

  useEffect(() => {
    setParentGroupFilter(ALL_PARENT_GROUPS);
    setSortConfig({ key: 'id', direction: 'asc' });
  }, [rows]);

  const displayedRows = useMemo(() => {
    const filtered =
      parentGroupFilter !== ALL_PARENT_GROUPS
        ? rows.filter((row) => row.parentGroupId === parentGroupFilter)
        : rows;

    const { key, direction } = sortConfig;
    return [...filtered].sort((a, b) =>
      compareSortValues(getSortValue(a, key), getSortValue(b, key), direction)
    );
  }, [rows, parentGroupFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  if (!visible) return null;

  return (
    <section className="nlp-grouping-card">
      <h2 className="nlp-section-title">5. Detailed Ticket Records with Sub-Group Tagging</h2>

      {rows.length === 0 ? (
        <p className="nlp-grouping-empty">No ticket records returned for this grouping run.</p>
      ) : (
        <>
          <div className="nlp-ticket-toolbar">
            <label className="nlp-ticket-filter">
              <span className="nlp-filter-label">Filter by Parent Group</span>
              <select
                value={parentGroupFilter}
                onChange={(e) => setParentGroupFilter(e.target.value)}
              >
                {parentGroupOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
            <span className="nlp-ticket-count">
              Showing {displayedRows.length} of {rows.length} tickets
            </span>
          </div>

          <div className="nlp-table-wrap nlp-table-wrap-wide">
            <table className="nlp-data-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`nlp-sortable-th${col.numeric ? ' nlp-th-num' : ''}`}
                      onClick={() => handleSort(col.key)}
                      aria-sort={
                        sortConfig.key === col.key
                          ? sortConfig.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <span className="nlp-th-label">
                        {col.label}
                        <SortIndicator
                          active={sortConfig.key === col.key}
                          direction={sortConfig.direction}
                        />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="nlp-grouping-empty-cell">
                      No tickets match the selected filter.
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((row, index) => (
                    <tr key={row.rowKey || `${row.id}-${index}`}>
                      <td className="nlp-td-mono">{row.id}</td>
                      <td>{row.macroArea}</td>
                      <td>{row.parentGroupId}</td>
                      <td>{row.subGroup}</td>
                      <td className="nlp-td-num">{Number(row.cumulative || 0).toFixed(2)}</td>
                      <td className="nlp-td-truncate" title={row.textAnswer}>{row.textAnswer}</td>
                      <td className="nlp-td-truncate" title={row.textRequest}>{row.textRequest}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export default Section5TicketRecords;
